import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { DashboardData, OrderStats, Sales } from '../utils/types';
import { restaurantService } from '../services';

interface RestaurantContextType {
  isRestaurantOpen: boolean;
  setIsRestaurantOpen: (value: boolean) => void;
  dashboardData: DashboardData | null;
  setDashboardData: (data: DashboardData | null) => void;
  orderStats: OrderStats | null;
  salesData: Sales | null;
  restaurantProfile: any | null;
  isLoadingProfile: boolean;
  refreshProfile: () => Promise<void>;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

export const RestaurantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isRestaurantOpen, setIsRestaurantOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [restaurantProfile, setRestaurantProfile] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true);

  const refreshProfile = async () => {
    try {
      const response = await restaurantService.getProfile();
      if (response.status && response.data) {
        setRestaurantProfile(response.data);
        setIsRestaurantOpen(response.data.isOpen ?? response.data.isActive ?? false);
      }
    } catch (error) {
      console.error('Error fetching restaurant profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Fetch initial restaurant profile on mount
  useEffect(() => {
    refreshProfile();
  }, []);

  const orderStats = dashboardData?.orderStats || null;
  const salesData = dashboardData?.sales || null;

  return (
    <RestaurantContext.Provider 
      value={{ 
        isRestaurantOpen, 
        setIsRestaurantOpen,
        dashboardData,
        setDashboardData,
        orderStats,
        salesData,
        restaurantProfile,
        isLoadingProfile,
        refreshProfile
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};
